import { useEffect, useState, type ReactNode } from "react";
import type { PhotoEvidence } from "../api/capture-types.js";
import { usePortal } from "../app/context.js";
import { utcMoment } from "../lib/format.js";

/**
 * The photographs taken during the survey, beside the scan rather than
 * below it.
 *
 * Asked for on 2026-09-09: "somewhere on the left side or on very right
 * side, the system should also show the original pictures those were taken
 * at the time of survey". Until now the portal listed a photograph's
 * subject, timestamp and GPS fix and showed a placeholder tile where the
 * picture should be, because no route existed to fetch the bytes. That
 * route landed in 428a513; this is the surface that uses it.
 *
 * Shows the STAMPED copy — the one with the date, time and position burned
 * into the pixels. That is the artefact that carries evidential weight, and
 * a designer comparing a wall in the 3D model against what was actually
 * there should be looking at the stamped frame, not a clean one that could
 * have come from anywhere.
 */
export function PhotoRail({
  photos,
}: {
  readonly photos: readonly PhotoEvidence[];
}): ReactNode {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useExpandedPreference(photos.length > 0);

  // Folded away: a slim tab that says how many there are, so hiding the
  // photographs never hides the fact that they exist. Asked for on
  // 2026-09-10 — "click and open the pictures if required, if not … close
  // the tab and hide them".
  if (!expanded) {
    return (
      <aside className="photo-rail is-collapsed" aria-label="Survey photographs">
        <button
          type="button"
          className="photo-rail__toggle photo-rail__toggle--tab"
          aria-expanded={false}
          onClick={() => setExpanded(true)}
          title="Show the survey photographs"
        >
          <CameraGlyph />
          <span className="photo-rail__tab-label">Photos</span>
          <span className="photo-rail__count">{photos.length}</span>
        </button>
      </aside>
    );
  }

  return (
    <>
      <aside className="photo-rail" aria-label="Survey photographs">
        <div className="photo-rail__head">
          <h2 className="photo-rail__title">
            Photographs <span className="photo-rail__count">{photos.length}</span>
          </h2>
          <button
            type="button"
            className="photo-rail__toggle"
            aria-expanded={true}
            onClick={() => setExpanded(false)}
            title="Hide the photographs and give the space back to the plan"
          >
            Hide
          </button>
        </div>
        {photos.length === 0 ? (
          <p className="photo-rail__empty">
            None yet. Photographs taken in the field app appear here once a
            device syncs.
          </p>
        ) : (
          <ul className="photo-rail__list">
            {photos.map((photo, index) => (
              <li key={photo.id}>
                <button
                  type="button"
                  className="photo-rail__tile"
                  onClick={() => setOpenIndex(index)}
                  title={`${photo.subject} — ${utcMoment(photo.capturedAt)}`}
                >
                  <PhotoImage photo={photo} />
                  <span className="photo-rail__meta">
                    <span className="photo-rail__subject">{photo.subject}</span>
                    <span className="photo-rail__when">
                      {utcMoment(photo.capturedAt)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>
      {openIndex !== null && photos[openIndex] ? (
        <Lightbox
          photos={photos}
          index={openIndex}
          onIndex={setOpenIndex}
          onClose={() => setOpenIndex(null)}
        />
      ) : null}
    </>
  );
}

const EXPANDED_KEY = "scannix.photoRail.expanded";

/**
 * Open or folded, remembered in this browser. With nothing stored, the rail
 * opens when there is something to look at and stays folded when there is
 * not. Storage can throw (private windows, blocked site data), so every
 * access is guarded and the default stands in.
 */
function useExpandedPreference(fallback: boolean): [boolean, (next: boolean) => void] {
  const [stored, setStored] = useState<boolean | null>(() => {
    try {
      const raw = window.localStorage.getItem(EXPANDED_KEY);
      return raw === null ? null : raw === "1";
    } catch {
      return null;
    }
  });
  const set = (next: boolean): void => {
    setStored(next);
    try {
      window.localStorage.setItem(EXPANDED_KEY, next ? "1" : "0");
    } catch {
      // Remembering is a convenience; the toggle still works without it.
    }
  };
  return [stored ?? fallback, set];
}

function CameraGlyph(): ReactNode {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M4 8h3l1.6-2.2A1.5 1.5 0 0 1 9.8 5h4.4a1.5 1.5 0 0 1 1.2.8L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="13" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

/**
 * One photograph's bytes, fetched through the authenticated client and
 * handed to an `<img>` as an object URL.
 *
 * The fetch cannot be skipped by pointing `src` straight at the API path:
 * every `/api` route requires an `Authorization: Bearer` header and an
 * `<img>` tag cannot send one.
 *
 * The object URL is revoked on unmount and whenever the photo changes. That
 * is not tidiness — an object URL pins its blob for the life of the
 * document, so a survey with forty photographs would otherwise hold forty
 * full-resolution JPEGs in memory across every navigation away from the
 * page.
 */
export function PhotoImage({
  photo,
  copy,
}: {
  readonly photo: PhotoEvidence;
  readonly copy?: "stamped" | "original";
}): ReactNode {
  const { client } = usePortal();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    setUrl(null);
    setFailed(false);

    client
      .photoImageObjectUrl(photo.id, copy ? { copy } : {})
      .then((made) => {
        // The effect may have been torn down while the bytes were in
        // flight. Revoke immediately rather than setting state on a
        // component that is gone — otherwise this blob leaks with no
        // handle left to release it.
        if (revoked) {
          URL.revokeObjectURL(made);
          return;
        }
        objectUrl = made;
        setUrl(made);
      })
      .catch(() => {
        if (!revoked) setFailed(true);
      });

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [client, photo.id, copy]);

  if (failed) {
    return (
      <span className="photo-rail__thumb photo-rail__thumb--failed">
        {/* A photograph the integrity check rejects is refused by the API as
            a 404 rather than served with a caveat, so this state means
            "these are not the bytes we recorded" as often as it means a
            network blip. Saying "unavailable" covers both honestly. */}
        Unavailable
      </span>
    );
  }

  if (!url) {
    return <span className="photo-rail__thumb photo-rail__thumb--loading" />;
  }

  return (
    <img
      className="photo-rail__thumb"
      src={url}
      alt={photo.subject}
      loading="lazy"
      decoding="async"
    />
  );
}

/**
 * A photograph at full size, with the detail that makes it evidence: what
 * it is of, when it was taken, and where the position came from. Arrow keys
 * (and the buttons) step through the survey's photographs without closing.
 */
function Lightbox({
  photos,
  index,
  onIndex,
  onClose,
}: {
  readonly photos: readonly PhotoEvidence[];
  readonly index: number;
  readonly onIndex: (index: number) => void;
  readonly onClose: () => void;
}): ReactNode {
  const photo = photos[index] as PhotoEvidence;
  const hasPrev = index > 0;
  const hasNext = index < photos.length - 1;

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
      else if (event.key === "ArrowLeft" && hasPrev) onIndex(index - 1);
      else if (event.key === "ArrowRight" && hasNext) onIndex(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onIndex, index, hasPrev, hasNext]);

  return (
    <div
      className="photo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`${photo.subject}, taken ${utcMoment(photo.capturedAt)}`}
      onClick={onClose}
    >
      <div
        className="photo-lightbox__panel"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="photo-lightbox__head">
          <div>
            <h3>{photo.subject}</h3>
            <p className="small">
              {utcMoment(photo.capturedAt)}
              {photo.description ? ` · ${photo.description}` : ""}
            </p>
          </div>
          <div className="photo-lightbox__nav">
            <span className="small photo-lightbox__position">
              {index + 1} of {photos.length}
            </span>
            <button
              type="button"
              className="secondary"
              onClick={() => onIndex(index - 1)}
              disabled={!hasPrev}
              aria-label="Previous photograph"
            >
              ←
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => onIndex(index + 1)}
              disabled={!hasNext}
              aria-label="Next photograph"
            >
              →
            </button>
            <button type="button" className="secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
        <div className="photo-lightbox__frame">
          <PhotoImage key={photo.id} photo={photo} />
        </div>
        <p className="photo-lightbox__foot small">
          Stamped copy — the date, time and position are printed into the
          image itself. Position source: {photo.positionSource}.
        </p>
      </div>
    </div>
  );
}
